import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  CircleHelp,
  Copy,
  Lightbulb,
  MessageSquareText,
  Search,
  Sparkles,
  TriangleAlert,
  X,
} from 'lucide-react';
import HelpVisual from './HelpVisual';
import { HELP_GROUPS } from './helpContent';
import WorkspaceHelpDrawer from './WorkspaceHelpDrawer';
import { replaceBrandValue, useTeamBrand } from '../branding/TeamBrandContext';

const DEFAULT_TOPIC = 'idea-to-script';
const CANVAS_FEEDBACK_TOPICS = new Set(['canvas-basics', 'character-images', 'scene-images', 'video-audio', 'assets-delivery']);

function HelpSection({ section }) {
  const brand = useTeamBrand();
  const [copyState, setCopyState] = useState('');
  const copyPrompt = async () => {
    setCopyState('copying');
    try {
      await navigator.clipboard.writeText(section.prompt);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <section id={section.id} className="scroll-mt-8 border-t border-black/[0.065] py-10 dark:border-white/[0.07]">
      <h2 className="text-[28px] font-semibold tracking-[-0.035em] text-[#2b272d] dark:text-white/90">{section.title}</h2>
      {section.paragraphs?.map(paragraph => <p key={paragraph} className="mt-4 text-[16px] leading-8 text-stone-600 dark:text-white/60">{paragraph}</p>)}

      {section.steps && (
        <ol className="mt-5 space-y-3">
          {section.steps.map((step, index) => (
            <li key={step} className="flex gap-3 text-[16px] leading-8 text-stone-600 dark:text-white/60">
              <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#e9e2ef] text-[14px] font-semibold text-[#6c5b80] dark:bg-[#bca8dc]/10 dark:text-[#cfbee8]">{index + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      )}

      {section.bullets && (
        <ul className="mt-5 space-y-2.5">
          {section.bullets.map(bullet => (
            <li key={bullet} className="flex gap-3 text-[16px] leading-8 text-stone-600 dark:text-white/60">
              <Check size={15} className="mt-1.5 shrink-0 text-[#7d6c91] dark:text-[#bba8d8]" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {section.prompt && (
        <div className="group relative mt-6 rounded-[18px] border border-[#8a78a3]/14 bg-[#f4f0f5] p-5 pr-14 text-[15px] leading-8 text-[#5d5265] dark:border-[#c3b0e2]/10 dark:bg-[#bca8dc]/[0.055] dark:text-[#d1c5dc]/70">
          <span className="mb-2 flex items-center gap-1.5 text-[14px] font-semibold tracking-[.08em] text-[#857394] dark:text-[#c4b1dd]/60"><Sparkles size={14} />可以直接参考</span>
          “{section.prompt}”
          <button type="button" onClick={copyPrompt} disabled={copyState === 'copying'} aria-label={copyState === 'copied' ? '已复制示例' : '复制示例'} className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-[#81718d] opacity-65 transition hover:bg-white hover:opacity-100 dark:hover:bg-white/[0.07]">{copyState === 'copied' ? <Check size={14} /> : <Copy size={14} />}</button>
          {copyState && <span className="mt-2 block text-[12px]" role={copyState === 'failed' ? 'alert' : 'status'}>{copyState === 'copying' ? '正在复制…' : copyState === 'copied' ? '已复制' : '未能复制，请选中文字手动复制。'}</span>}
        </div>
      )}

      {section.tip && (
        <div className="mt-6 flex gap-3 rounded-[18px] border border-[#bb9e67]/13 bg-[#f5efe2] p-4 text-[15px] leading-7 text-[#7a6749] dark:border-amber-200/10 dark:bg-amber-100/[0.045] dark:text-amber-100/60">
          <Lightbulb size={16} className="mt-1 shrink-0" />
          <p><strong className="mr-1 font-semibold">{brand.text('小影碎碎念：')}</strong>{section.tip}</p>
        </div>
      )}

      {section.warning && (
        <div className="mt-6 flex gap-3 rounded-[18px] border border-[#b77a70]/13 bg-[#f3e7e3] p-4 text-[15px] leading-7 text-[#865e57] dark:border-rose-200/10 dark:bg-rose-100/[0.045] dark:text-rose-100/60">
          <TriangleAlert size={16} className="mt-1 shrink-0" />
          <p>{section.warning}</p>
        </div>
      )}

      {section.visual && <div className="mt-7 empty:hidden"><HelpVisual type={section.visual} /></div>}
    </section>
  );
}

export default function HelpCenterPage() {
  const brand = useTeamBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preview = false, user = null, userLoading = false } = useOutletContext() || {};
  const navigate = useNavigate();
  const helpGroups = useMemo(
    () => replaceBrandValue(HELP_GROUPS, brand.active ? brand.name : ''),
    [brand.active, brand.name],
  );
  const helpTopics = useMemo(
    () => helpGroups.flatMap(group => group.items.map(item => ({ ...item, group: group.title }))),
    [helpGroups],
  );
  const requestedTopic = searchParams.get('topic');
  const activeTopic = helpTopics.find(topic => topic.id === requestedTopic) || helpTopics.find(topic => topic.id === DEFAULT_TOPIC);
  const activeIndex = helpTopics.findIndex(topic => topic.id === activeTopic.id);
  const [query, setQuery] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const searchResults = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase('zh-CN');
    if (!keyword) return [];
    return helpTopics.filter(topic => [topic.title, topic.summary, topic.group, topic.intro, ...topic.sections.flatMap(section => [section.title, ...(section.paragraphs || []), ...(section.steps || []), ...(section.bullets || [])])].join(' ').toLocaleLowerCase('zh-CN').includes(keyword));
  }, [helpTopics, query]);

  const chooseTopic = topicId => {
    setSearchParams({ topic: topicId });
    setQuery('');
  };

  const previousTopic = helpTopics[activeIndex - 1];
  const nextTopic = helpTopics[activeIndex + 1];
  const homePath = preview ? '/prototype/vnext' : '/home';
  const feedbackScene = CANVAS_FEEDBACK_TOPICS.has(activeTopic.id) ? 'canvas' : 'writing';
  const authenticated = preview || Boolean(user) || Boolean(localStorage.getItem('token'));

  const openFeedback = () => {
    if (!authenticated) {
      const returnTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      navigate('/login', { state: { from: returnTarget } });
      return;
    }
    setFeedbackOpen(true);
  };

  return (
    <div className="min-h-full bg-[var(--vnext-page)]">
      <header className="sticky top-0 z-30 border-b border-black/[0.06] bg-[var(--vnext-page)]/92 backdrop-blur-xl dark:border-white/[0.07]">
        <div className="mx-auto flex h-[72px] max-w-[1480px] items-center gap-4 pl-5 pr-16 sm:px-8">
          <button type="button" onClick={() => navigate(homePath)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-stone-500 transition hover:bg-black/[0.04] hover:text-stone-900 dark:text-white/40 dark:hover:bg-white/[0.06] dark:hover:text-white" aria-label="返回首页"><ArrowLeft size={17} /></button>
          <div className="hidden items-center gap-2 sm:flex"><CircleHelp size={19} className="text-[#7d6d92]" /><strong className="text-[15px] font-semibold">{brand.text('小影帮助中心')}</strong></div>
          <div className="relative ml-auto min-w-0 flex-1 max-w-[470px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-white/30" size={15} />
            <input value={query} onChange={event => setQuery(event.target.value)} aria-label="搜索帮助" placeholder="搜索：人物图、粘贴剧本、Agent……" className="h-11 w-full rounded-[14px] border border-black/[0.07] bg-white/66 pl-10 pr-10 text-[14px] outline-none transition placeholder:text-stone-400 focus:border-[#8a78a3]/24 focus:ring-4 focus:ring-[#8a78a3]/[0.055] dark:border-white/[0.08] dark:bg-white/[0.045] dark:text-white/80 dark:placeholder:text-white/40" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="清空帮助搜索" className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-stone-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"><X size={13} /></button>}
            {query && (
              <div className="absolute left-0 right-0 top-12 z-50 max-h-[360px] overflow-auto rounded-[18px] border border-black/[0.08] bg-[#fffdfa] p-2 shadow-2xl dark:border-white/[0.09] dark:bg-[#211e25]">
                {searchResults.length ? searchResults.map(topic => (
                  <button type="button" key={topic.id} onClick={() => chooseTopic(topic.id)} className="flex w-full items-start gap-3 rounded-[13px] px-3 py-3 text-left transition hover:bg-black/[0.04] dark:hover:bg-white/[0.055]">
                    <BookOpen size={14} className="mt-0.5 shrink-0 text-[#827194]" />
                    <span className="min-w-0"><strong className="block text-[15px] font-semibold">{topic.title}</strong><small className="mt-1 block line-clamp-1 text-[14px] text-stone-400 dark:text-white/40">{topic.summary}</small></span>
                  </button>
                )) : <div className="px-4 py-8 text-center text-[14px] text-stone-400">{brand.text('没搜到。换个说法，小影还没学会读心。')}</div>}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={openFeedback}
            disabled={!preview && userLoading}
            aria-label="提交反馈"
            aria-controls="workspace-help-drawer"
            aria-expanded={feedbackOpen}
            title={!preview && userLoading ? '正在确认登录状态' : undefined}
            className="flex h-11 min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-[14px] bg-[#3a3340] px-3 text-white shadow-sm transition hover:bg-[#2e2834] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#8a78a3]/20 disabled:cursor-wait disabled:opacity-50 dark:bg-[#c5b3df] dark:text-[#251f2b] dark:hover:bg-[#d2c2e8] sm:px-4"
          >
            <MessageSquareText size={15} />
            <span className="hidden whitespace-nowrap text-[14px] font-semibold sm:inline">提交反馈</span>
          </button>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1480px] grid-cols-1 px-5 sm:px-8 lg:grid-cols-[230px_minmax(0,780px)] lg:gap-10 xl:grid-cols-[230px_minmax(0,780px)_190px] xl:gap-12">
        <aside className="hidden border-r border-black/[0.055] py-10 pr-5 dark:border-white/[0.06] lg:block" aria-label="帮助主题">
          <nav className="sticky top-[104px] max-h-[calc(100vh-130px)] space-y-7 overflow-y-auto pr-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {helpGroups.map(group => (
              <section key={group.title}>
                <h2 className="px-3 text-[14px] font-semibold tracking-[.08em] text-stone-400 dark:text-white/40">{group.title}</h2>
                <div className="mt-2 space-y-1">
                  {group.items.map(topic => {
                    const active = topic.id === activeTopic.id;
                    return (
                      <button type="button" key={topic.id} onClick={() => chooseTopic(topic.id)} className={`group flex w-full items-center gap-2 rounded-[12px] px-3 py-2.5 text-left text-[14px] transition ${active ? 'bg-black/[0.055] font-semibold text-[#302b33] dark:bg-white/[0.075] dark:text-white/90' : 'text-stone-500 hover:bg-black/[0.03] hover:text-stone-800 dark:text-white/50 dark:hover:bg-white/[0.04] dark:hover:text-white/70'}`}>
                        <span className="min-w-0 flex-1">{topic.title}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 py-10 lg:py-14">
          <div className="mb-6 flex gap-2 overflow-x-auto pb-1 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {helpTopics.map(topic => <button type="button" key={topic.id} onClick={() => chooseTopic(topic.id)} className={`shrink-0 rounded-full border px-3.5 py-2.5 text-[14px] ${topic.id === activeTopic.id ? 'border-transparent bg-[#352f3b] text-white dark:bg-[#c1afe3] dark:text-[#241e29]' : 'border-black/[0.07] text-stone-500 dark:border-white/[0.08] dark:text-white/50'}`}>{topic.title}</button>)}
          </div>

          <article>
            <div className="text-[14px] font-semibold tracking-[.1em] text-[#806e95] dark:text-[#c5b3df]/70">{activeTopic.eyebrow}</div>
            <h1 className="mt-3 text-[44px] font-semibold leading-[1.12] tracking-[-0.052em] text-[#27232a] dark:text-white/90 sm:text-[54px]">{activeTopic.title}</h1>
            <p className="mt-5 max-w-[720px] text-[17px] leading-9 text-stone-500 dark:text-white/60">{activeTopic.intro}</p>

            <div className="mt-9 rounded-[22px] border border-[#927fa8]/12 bg-gradient-to-br from-[#ede6f1] via-[#f2ece8] to-[#e7ece7] p-5 dark:border-white/[0.07] dark:from-[#2c2731] dark:via-[#29262c] dark:to-[#252b29]">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[13px] bg-white/65 text-[#746287] shadow-sm dark:bg-white/[0.06] dark:text-[#c8b5e2]"><Sparkles size={16} /></span>
                <div><strong className="text-[16px] font-semibold">这一篇能帮你什么</strong><p className="mt-1.5 text-[15px] leading-7 text-stone-500 dark:text-white/50">{activeTopic.summary}</p></div>
              </div>
            </div>

            <div className="mt-5">
              {activeTopic.sections.map(section => <HelpSection key={section.id} section={section} />)}
            </div>

            <div className="mt-2 grid gap-3 border-t border-black/[0.065] pt-8 dark:border-white/[0.07] sm:grid-cols-2">
              {previousTopic ? <button type="button" onClick={() => chooseTopic(previousTopic.id)} className="group rounded-[18px] border border-black/[0.07] bg-white/45 p-5 text-left transition hover:bg-white dark:border-white/[0.08] dark:bg-white/[0.025] dark:hover:bg-white/[0.05]"><span className="flex items-center gap-2 text-[14px] text-stone-400"><ArrowLeft size={14} />上一篇</span><strong className="mt-2 block text-[16px] font-semibold">{previousTopic.title}</strong></button> : <div />}
              {nextTopic && <button type="button" onClick={() => chooseTopic(nextTopic.id)} className="group rounded-[18px] border border-black/[0.07] bg-white/45 p-5 text-right transition hover:bg-white dark:border-white/[0.08] dark:bg-white/[0.025] dark:hover:bg-white/[0.05]"><span className="flex items-center justify-end gap-2 text-[14px] text-stone-400">下一篇<ArrowRight size={14} /></span><strong className="mt-2 block text-[16px] font-semibold">{nextTopic.title}</strong></button>}
            </div>

          </article>
        </main>

        <aside className="hidden py-14 xl:block" aria-label="本页内容">
          <nav className="sticky top-[104px] border-l border-black/[0.06] pl-5 dark:border-white/[0.07]">
            <h2 className="text-[14px] font-semibold tracking-[.08em] text-stone-400 dark:text-white/40">本页内容</h2>
            <div className="mt-4 space-y-1.5">
              {activeTopic.sections.map(section => <a key={section.id} href={`#${section.id}`} className="group flex items-start gap-2 py-2 text-[14px] leading-6 text-stone-400 transition hover:text-[#655573] dark:text-white/40 dark:hover:text-white/70"><ChevronRight size={14} className="mt-1 shrink-0 opacity-45" />{section.title}</a>)}
            </div>
          </nav>
        </aside>
      </div>
      <WorkspaceHelpDrawer
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        scene={feedbackScene}
        preview={preview}
        initialTab="feedback"
        sourceLabel="帮助中心"
      />
    </div>
  );
}
